import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Link2, X, Loader2, Inbox } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["New", "Reviewing", "Accepted", "Implemented", "Declined", "Archived"];
const TYPES = ["Article / Topic", "AI Tool", "AI Model", "Technology / Product", "Gaming",
  "Review", "Guide / Tutorial", "Deal / Product", "Website Feature", "Correction / Fact Check", "Other"];
const STATUS_STYLE = {
  New: "bg-klyroo-red/15 text-klyroo-red", Reviewing: "bg-amber-400/15 text-amber-300",
  Accepted: "bg-klyroo-cyan/15 text-klyroo-cyan", Implemented: "bg-emerald-400/15 text-emerald-300",
  Declined: "bg-slate-500/15 text-slate-400", Archived: "bg-white/5 text-slate-500",
};
const inputCls = "bg-klyroo-surface border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-klyroo-cyan/50";

export function SuggestionsInbox({ onChange }) {
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("All");
  const [type, setType] = useState("All");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    setItems(null);
    api.adminSuggestions({ status, suggestion_type: type, search, sort })
      .then((d) => { setItems(d.items); setCounts(d.counts || {}); })
      .catch(() => setItems([]));
  }, [status, type, search, sort]);
  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search suggestions…" data-testid="sugg-search" className={`${inputCls} pl-3 w-56`} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sugg-filter-status" className={inputCls}>
          <option value="All">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}{counts[s] != null ? ` (${counts[s]})` : ""}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="sugg-filter-type" className={inputCls}>
          <option value="All">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sugg-sort" className={inputCls}>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>

      {!items ? <div className="py-16 text-center text-slate-500"><Loader2 className="animate-spin mx-auto" /></div>
        : items.length === 0 ? (
          <div className="py-16 text-center text-slate-500" data-testid="sugg-empty"><Inbox className="mx-auto mb-2" /> No suggestions{status !== "All" || type !== "All" || search ? " match these filters." : " yet."}</div>
        ) : (
          <div className="space-y-2" data-testid="sugg-list">
            {items.map((s) => (
              <button key={s.id} onClick={() => setActive(s)} data-testid={`sugg-row-${s.id}`}
                className="w-full text-left flex items-center gap-3 card-surface rounded-xl px-4 py-3 hover:bg-white/[0.06] transition">
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-klyroo-cyan/10 text-klyroo-cyan shrink-0 w-36 truncate">{s.suggestion_type}</span>
                <span className="flex-1 min-w-0 text-slate-200 text-sm truncate">{s.suggestion}</span>
                <span className="text-xs text-slate-400 w-24 truncate hidden sm:block">{s.name || "—"}</span>
                {s.email && <Mail size={13} className="text-slate-500 shrink-0" title="Email provided" />}
                {s.reference_url && <Link2 size={13} className="text-slate-500 shrink-0" title="Has reference link" />}
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded shrink-0 ${STATUS_STYLE[s.status]}`}>{s.status}</span>
                <span className="text-xs font-mono text-slate-500 w-14 text-right shrink-0">{fmt(s.created_at)}</span>
              </button>
            ))}
          </div>
        )}

      {active && <DetailPanel s={active} onClose={() => setActive(null)}
        onSaved={() => { setActive(null); load(); onChange && onChange(); }} />}
    </div>
  );
}

function DetailPanel({ s, onClose, onSaved }) {
  const [status, setStatus] = useState(s.status);
  const [notes, setNotes] = useState(s.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      await api.updateSuggestion(s.id, { status: nextStatus || status, admin_notes: notes });
      toast.success("Suggestion updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="sugg-detail">
      <div className="w-full max-w-md h-full bg-klyroo-bg border-l border-white/10 overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <span className="text-[10px] font-mono px-2 py-1 rounded bg-klyroo-cyan/10 text-klyroo-cyan">{s.suggestion_type}</span>
          <button onClick={onClose} data-testid="sugg-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <Row label="Suggestion"><p className="text-white whitespace-pre-wrap">{s.suggestion}</p></Row>
        {s.reason && <Row label="Why they suggested it"><p className="text-slate-300 whitespace-pre-wrap">{s.reason}</p></Row>}
        {s.reference_url && <Row label="Reference"><a href={s.reference_url} target="_blank" rel="noopener noreferrer" className="text-klyroo-cyan break-all hover:underline">{s.reference_url}</a></Row>}
        <div className="grid grid-cols-2 gap-3">
          <Row label="Name"><p className="text-slate-300">{s.name || "—"}</p></Row>
          <Row label="Email">{s.email ? <a href={`mailto:${s.email}`} className="text-klyroo-cyan break-all">{s.email}</a> : <p className="text-slate-500">—</p>}</Row>
        </div>
        <Row label="Submitted"><p className="text-slate-400 text-sm">{new Date(s.created_at).toLocaleString()}</p></Row>

        <div className="border-t border-white/10 mt-4 pt-4">
          <label className="text-xs font-medium text-slate-300">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sugg-status-select" className={`${inputCls} w-full mt-1.5`}>
            {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>

          <label className="text-xs font-medium text-slate-300 mt-4 block">Admin notes <span className="text-slate-500">(private)</span></label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="sugg-notes"
            placeholder="Internal notes — never shown publicly" className={`${inputCls} w-full mt-1.5`} />

          <div className="flex flex-wrap gap-2 mt-4">
            <button disabled={saving} onClick={() => save()} data-testid="sugg-save" className="px-4 py-2 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white disabled:opacity-50">Save</button>
            <button disabled={saving} onClick={() => save("Reviewing")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10">Reviewing</button>
            <button disabled={saving} onClick={() => save("Accepted")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10">Accept</button>
            <button disabled={saving} onClick={() => save("Implemented")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10">Implemented</button>
            <button disabled={saving} onClick={() => save("Declined")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10">Decline</button>
            <button disabled={saving} onClick={() => save("Archived")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-500 text-sm hover:bg-white/10">Archive</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return <div className="mb-3"><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</div>;
}
